import React, { useState, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import MaterialIcon from './MaterialIcon';
import { COLOR_FILTERS, COLOR_NAMES } from '../data/colors';
import { GRID_CONSTANTS } from '../config/constants';
import { trackSceneBuilderAction } from '../services/analyticsService';
import { decodeHtmlEntities } from '../utils/htmlEntityDecoder';
import { buildEtsyVariationUrl } from '../utils/etsyVariationUrls';

const INCH_TO_PX = GRID_CONSTANTS.INCH_TO_PX;
const GRID_MAX_PX = GRID_CONSTANTS.GRID_MAX_PX;
const GLOBAL_ZOOM = GRID_CONSTANTS.GLOBAL_ZOOM;

// Get the appropriate image source for a sticker (same logic as ActualSizeSticker)
const getStickerImageSource = (sticker) => {
  // First priority: svgPath from database (with path resolution)
  if (sticker.svgPath) {
    let resolvedPath;
    // Handle different path formats
    if (sticker.svgPath.startsWith('/stickers/')) {
      // Convert old sticker paths to new asset paths
      const fileName = sticker.svgPath.split('/').pop();
      const category = sticker?.category?.toLowerCase() || 'dinosaurs';

      // Special case for T-Rex - use the actual filename that exists
      let finalFileName = fileName.replace('.svg', '.png');
      if (fileName === 't-rex.svg') {
        finalFileName = 'trex-2.png';
      }

      resolvedPath = `/assets/images/${category}/${finalFileName}`;
    } else if (sticker.svgPath.startsWith('/assets/')) {
      // Already in correct format
      resolvedPath = sticker.svgPath;
    } else {
      // Assume it's a relative path
      resolvedPath = sticker.svgPath;
    }

    return resolvedPath;
  }

  // Second priority: size-specific image from Etsy using existing mapping logic (same as ActualSizeSticker)
  if (sticker?.images && sticker?.variationImages) {
    // Handle both array and object formats for images
    const images = Array.isArray(sticker.images) ? sticker.images : (sticker.images.results || []);
    const variationImages = sticker.variationImages;

    // Use the same logic as CoreStickerCard to find the correct image for the selected size
    if (variationImages.results && variationImages.results.length > 0) {
      // Use the selected size (first token) to find the correct image
      const selectedSizeName = String(sticker.size || '')
        .split(/\s+/)[0]
        .toUpperCase();

      // Find the variation for the selected size
      const sizeVariation = variationImages.results.find(v => {
        const vSizeMatch = v.value.match(/^(\w+)/);
        return vSizeMatch && vSizeMatch[1].toUpperCase() === selectedSizeName;
      });

      if (sizeVariation) {
        const image = images.find(img => img.listing_image_id === sizeVariation.image_id);
        if (image) {
          return image.url_fullxfull;
        }
      }
    }

    // Fallback to index-based correlation using actual available sizes
    const availableSizes = sticker?.availableSizes || [];
    const sizeIndex = availableSizes.indexOf(sticker.size.toUpperCase());

    if (sizeIndex >= 0 && images[sizeIndex + 1]) {
      return images[sizeIndex + 1].url_fullxfull;
    }
  }

  // Third priority: first image from Etsy data
  if (sticker?.image && sticker.image.startsWith('http')) {
    return sticker.image;
  }

  // Fallback: use svgPath as is
  return sticker.svgPath;
};

const DraggableSticker = ({
  sticker,
  widthPx,
  heightPx,
  onRemove,
  onUpdateSticker,
  showDetails = false,
  zoomScale,
  allStickers,
  adminMode = false,
  onDragStateChange
}) => {
  // Get the appropriate image source for this sticker
  const imageSource = getStickerImageSource(sticker);

  const nodeRef = useRef(null);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const [showButtonDelayed, setShowButtonDelayed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const hideTimeoutRef = useRef(null);
  const buttonTimeoutRef = useRef(null);

  // Performance optimization: Track position locally during drag
  const dragPositionRef = useRef({ x: sticker.x, y: sticker.y });
  const lastUpdateTimeRef = useRef(0);
  const UPDATE_THROTTLE = 16; // ~60fps throttling

  // Calculate real-world dimensions in inches
  const widthInches = widthPx / (INCH_TO_PX * GLOBAL_ZOOM);
  const heightInches = heightPx / (INCH_TO_PX * GLOBAL_ZOOM);

  const handleRemoveClick = useCallback((e) => {
    e.stopPropagation();

    // Track sticker removal for analytics
    trackSceneBuilderAction('remove_sticker', sticker.listingId, sticker.category, sticker.title);

    onRemove(sticker.id);
  }, [onRemove, sticker.id, sticker.listingId, sticker.category, sticker.title]);

  const handleBringToFront = useCallback((e) => {
    e.stopPropagation();
    if (onUpdateSticker) {
      // Find the highest z-index among all stickers
      const maxZ = Math.max(...allStickers.map(s => s.zIndex || 1));
      onUpdateSticker(sticker.id, { zIndex: maxZ + 1 });
    }
  }, [onUpdateSticker, sticker.id, allStickers]);

  const handleMouseEnter = useCallback(() => {
    setShowMeasurements(true);
    setShowButtonDelayed(true);

    // Clear any existing timeout
    if (buttonTimeoutRef.current) {
      clearTimeout(buttonTimeoutRef.current);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowMeasurements(false);
    // Keep button visible for 500ms after mouse leave
    buttonTimeoutRef.current = setTimeout(() => {
      setShowButtonDelayed(false);
    }, 500);
  }, []);

  const bringToFront = useCallback(() => {
    if (onUpdateSticker && allStickers && allStickers.length > 0) {
      const maxZ = Math.max(...allStickers.map(s => s.zIndex || 1));
      if ((sticker.zIndex || 1) <= maxZ) {
        onUpdateSticker(sticker.id, { zIndex: maxZ + 2 });
      }
    }
  }, [onUpdateSticker, allStickers, sticker.id, sticker.zIndex]);

  const handleTouchStart = (e) => {
    // Handle touch events for mobile
    if (e.touches && e.touches.length === 1) {
      setShowMeasurements(true);
      // Bring to front on touch
      bringToFront();
    }
  };

  const handleTouchEnd = () => {
    // Delay hiding measurements on touch devices
    setTimeout(() => {
      setShowMeasurements(false);
    }, 1000);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    // Find overlapping stickers at this position
    const overlappingStickers = allStickers.filter(s => {
      // Simple overlap detection based on position and size
      const stickerRect = {
        x: s.x,
        y: s.y,
        width: 80, // Approximate sticker width
        height: 80 // Approximate sticker height
      };
      const thisRect = {
        x: sticker.x,
        y: sticker.y,
        width: 80,
        height: 80
      };

      return (
        stickerRect.x < thisRect.x + thisRect.width &&
        stickerRect.x + stickerRect.width > thisRect.x &&
        stickerRect.y < thisRect.y + thisRect.height &&
        stickerRect.y + stickerRect.height > thisRect.y
      );
    });

    if (overlappingStickers.length > 1) {
      // Cycle through z-indexes of overlapping stickers
      const currentZ = sticker.zIndex || 1;
      const maxZ = Math.max(...overlappingStickers.map(s => s.zIndex || 1));
      const newZ = currentZ >= maxZ ? 1 : currentZ + 1;

      if (onUpdateSticker) {
        onUpdateSticker(sticker.id, { zIndex: newZ });
      }
    } else {
      // No overlap, just bring to front
      handleBringToFront(e);
    }
  };

  // Calculate z-index: use sticker's zIndex
  const baseZIndex = sticker.zIndex || 1;
  const zIndex = baseZIndex;

  // Memoize position to prevent unnecessary re-renders
  const position = useMemo(() => ({ x: sticker.x, y: sticker.y }), [sticker.x, sticker.y]);

  // Memoize bounds calculation to prevent unnecessary re-renders
  const bounds = useMemo(() => ({
    left: 0,
    top: 0,
    right: (GRID_MAX_PX / zoomScale) - (widthPx / zoomScale),
    bottom: (GRID_MAX_PX / zoomScale) - (heightPx / zoomScale)
  }), [zoomScale, widthPx, heightPx]);

  return (
    <Draggable
      nodeRef={nodeRef}
      position={position}
      bounds={bounds}
      cancel=".no-drag"
      enableUserSelectHack={false}
      allowAnyClick={true}
      onStart={(e, data) => {
        // Ensure touch events work properly
        if (e.type === 'touchstart') {
          e.preventDefault();
        }
        // Hide details during drag
        setIsDragging(true);
        // Bring to front when dragging starts
        bringToFront();
        // Notify parent of drag state change
        if (onDragStateChange) {
          onDragStateChange(true);
        }
        // Initialize drag position
        dragPositionRef.current = { x: data.x, y: data.y };
      }}
      onDrag={(e, data) => {
        // Update local position immediately for smooth dragging
        dragPositionRef.current = { x: data.x, y: data.y };

        // Throttle state updates to improve performance
        const now = Date.now();
        if (now - lastUpdateTimeRef.current > UPDATE_THROTTLE) {
          lastUpdateTimeRef.current = now;
          if (onUpdateSticker) {
            onUpdateSticker(sticker.id, { x: data.x, y: data.y });
          }
        }
      }}
      onStop={(e, data) => {
        // Final position update when drag ends
        if (onUpdateSticker) {
          onUpdateSticker(sticker.id, { x: data.x, y: data.y });
        }
        // Show details again when drag ends
        setIsDragging(false);
        // Notify parent of drag state change
        if (onDragStateChange) {
          onDragStateChange(false);
        }
      }}
    >
      <div
        ref={nodeRef}
        className="draggable-sticker"
        style={{
          zIndex
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onContextMenu={handleContextMenu}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'f' || e.key === 'F') {
            e.preventDefault();
            handleBringToFront(e);
          }
          if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            handleRemoveClick(e);
          }
        }}
        role="button"
        aria-label={`${decodeHtmlEntities(sticker.title)} sticker, press F to bring to front, press Delete to remove`}
      >
        <div>
          <div className={`sticker-container`} style={{ position: 'relative' }}>
            <div className="sticker-item" style={{ position: 'relative' }}>
              {/* Remove (X) button - hidden during drag */}
              {!isDragging && (
                <button
                  className="sticker-remove-btn no-drag"
                  title="Remove from scene"
                  onClick={handleRemoveClick}
                >
                  <MaterialIcon icon="close" size={16} />
                </button>
              )}
              {/* Shadow element (behind, subtle gray, offset) */}
              <img
                src={imageSource}
                alt=""
                aria-hidden="true"
                className={`scene-sticker-shadow ${isDragging ? 'dragging' : ''} ${sticker.isFlipped ? 'flipped' : ''}`}
                style={{
                  width: widthPx,
                  height: heightPx
                }}
              />
              {/* Actual sticker image (on top, colored) */}
              <img
                src={imageSource}
                alt={decodeHtmlEntities(sticker.title)}
                className={`scene-sticker-svg ${isDragging ? 'dragging' : ''} ${sticker.isFlipped ? 'flipped' : ''}`}
                style={{
                  width: widthPx,
                  height: heightPx,
                  filter: COLOR_FILTERS[sticker.color] || COLOR_FILTERS['Black (Matte)'] || 'brightness(0) saturate(100%)',
                  transform: sticker.isFlipped ? 'scaleX(-1)' : 'none'
                }}
                onError={(e) => {
                  // Image failed to load - show placeholder
                  console.error('❌ Image failed to load:', imageSource);
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
                onLoad={() => {
                  // Image loaded successfully
                }}
              />

              <div
                className="scene-sticker-placeholder"
                style={{
                  display: 'none',
                  position: 'relative',
                  top: isDragging ? '-2px' : '0px',
                  left: isDragging ? '-2px' : '0px',
                  transition: isDragging ? 'all 0.1s ease-out' : 'none'
                }}
              >
                <span>{sticker.title.length > 10 ? `${decodeHtmlEntities(sticker.title.substring(0, 10))}...` : decodeHtmlEntities(sticker.title)}</span>
              </div>

              {/* Measurement overlay on hover or when details are enabled - hidden during drag */}
              {!isDragging && (showMeasurements || showDetails) && (
                <div className="measurement-overlay">
                  {/* Width measurement (top) */}
                  <div
                    className="measurement-line width-line"
                    style={{
                      position: 'absolute',
                      top: '-25px',
                      left: '0',
                      width: widthPx,
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {/* Left arrow */}
                    <div style={{
                      position: 'absolute',
                      left: '0',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '0',
                      height: '0',
                      borderTop: '8px solid transparent',
                      borderBottom: '8px solid transparent',
                      borderRight: '12px solid #7f8c8d'
                    }} />

                    {/* Horizontal line */}
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '12px',
                      right: '12px',
                      height: '3px',
                      backgroundColor: '#7f8c8d',
                      transform: 'translateY(-50%)'
                    }} />

                    {/* Right arrow */}
                    <div style={{
                      position: 'absolute',
                      right: '0',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '0',
                      height: '0',
                      borderTop: '8px solid transparent',
                      borderBottom: '8px solid transparent',
                      borderLeft: '12px solid #7f8c8d'
                    }} />

                    {/* Measurement label */}
                    <span
                      className="measurement-label"
                      style={{
                        position: 'absolute',
                        top: '-18px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'transparent',
                        color: '#000',
                        padding: '0',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        textAlign: 'center'
                      }}
                    >
                      {widthInches.toFixed(1)}"<br/>
                      <span style={{ fontSize: '10px', fontWeight: 'normal', color: '#7f8c8d' }}>APPROX.</span>
                    </span>
                  </div>

                  {/* Height measurement (left) */}
                  <div
                    className="measurement-line height-line"
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: '-25px',
                      width: '20px',
                      height: heightPx,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column'
                    }}
                  >
                    {/* Top arrow */}
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '0',
                      height: '0',
                      borderLeft: '8px solid transparent',
                      borderRight: '8px solid transparent',
                      borderBottom: '12px solid #7f8c8d'
                    }} />

                    {/* Vertical line */}
                    <div style={{
                      position: 'absolute',
                      left: '50%',
                      top: '12px',
                      bottom: '12px',
                      width: '3px',
                      backgroundColor: '#7f8c8d',
                      transform: 'translateX(-50%)'
                    }} />

                    {/* Bottom arrow */}
                    <div style={{
                      position: 'absolute',
                      bottom: '0',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '0',
                      height: '0',
                      borderLeft: '8px solid transparent',
                      borderRight: '8px solid transparent',
                      borderTop: '12px solid #7f8c8d'
                    }} />

                    {/* Measurement label */}
                    <span
                      className="measurement-label"
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '-25px',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'transparent',
                        color: '#000',
                        padding: '0',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        transformOrigin: 'center'
                      }}
                    >
                      {heightInches.toFixed(1)}"<br/>
                      <span style={{ fontSize: '10px', fontWeight: 'normal', color: '#7f8c8d' }}>APPROX.</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Title visibility: hidden by default, show on hover OR when details toggle is ON - hidden during drag */}
              {!isDragging && (
                <div className={`sticker-title-wrapper ${showMeasurements ? 'details-expanded' : ''} ${showDetails ? 'details-visible' : ''} ${showMeasurements || showDetails ? 'show-title' : ''}`}>
                  {/* Buy it NOW button - centered above title chip */}
                  {sticker.etsyUrl && (showMeasurements || showDetails) && (
                    <button
                      className="scene-buy-now-btn no-drag"
                      onClick={(e) => {
                        e.stopPropagation();

                        // Build variation URL if we have the data
                        const variationUrl = buildEtsyVariationUrl(
                          sticker.etsyUrl,
                          sticker.sizeVariations,
                          sticker.colorVariations,
                          sticker.size,
                          sticker.color
                        );

                        const finalUrl = variationUrl || sticker.etsyUrl;

                        // Track Etsy click for analytics
                        trackSceneBuilderAction(
                          'buy_now_click_scene',
                          sticker.listingId,
                          sticker.category,
                          sticker.title
                        );

                        window.open(finalUrl, '_blank');
                      }}
                      title="Buy it NOW!"
                    >
                      BUY IT NOW!
                    </button>
                  )}
                  <div
                    className={`sticker-title category-${sticker.category?.toLowerCase().replace(/\s+/g, '-').replace(/[\/&]/g, '-')}`}
                  >
                    <span className="drag-icon">⋮⋮</span>
                    <span className="title-base">
                      {sticker.title.length > 10 ? `${decodeHtmlEntities(sticker.title.substring(0, 10))}...` : decodeHtmlEntities(sticker.title)}
                    </span>
                    {/* Details (size/color dropdowns) */}
                    {showDetails && (
                      <span className="title-details">
                        {sticker.size && (
                          <>
                            <span className="separator"> | </span>
                            <select
                              className="title-dropdown size-dropdown no-drag"
                              value={sticker.size}
                              onChange={async (e) => {
                                e.stopPropagation();
                                const newSize = e.target.value;

                                // Track size change for analytics
                                trackSceneBuilderAction(
                                  `change_sticker_size_scene_to_${newSize}`,
                                  sticker.listingId,
                                  sticker.category,
                                  sticker.title
                                );

                                // Update the sticker size
                                onUpdateSticker(sticker.id, { size: newSize });
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {sticker.availableSizes && sticker.availableSizes.length > 0 ? (
                                sticker.availableSizes.map(size => (
                                  <option key={size} value={decodeHtmlEntities(size)}>
                                    {decodeHtmlEntities(size)}
                                  </option>
                                ))
                              ) : (
                                <option value="MICRO">MICRO</option>
                              )}
                            </select>
                          </>
                        )}
                        {sticker.color && (
                          <>
                            <span className="separator"> | </span>
                            <select
                              className="title-dropdown color-dropdown no-drag"
                              value={sticker.color}
                              onChange={(e) => {
                                e.stopPropagation();
                                const newColor = e.target.value;

                                // Track color change for analytics
                                trackSceneBuilderAction(
                                  `change_sticker_color_scene_to_${newColor.replace(/[^a-zA-Z0-9]/g, '_')}`,
                                  sticker.listingId,
                                  sticker.category,
                                  sticker.title
                                );

                                onUpdateSticker(sticker.id, { color: newColor });
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {sticker.availableColors && sticker.availableColors.length > 0 ? (
                                sticker.availableColors.map(color => (
                                  <option key={color} value={color}>
                                    {decodeHtmlEntities(color)}
                                  </option>
                                ))
                              ) : (
                                COLOR_NAMES.map(colorName => (
                                  <option key={colorName} value={colorName}>
                                    {colorName}
                                  </option>
                                ))
                              )}
                            </select>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Draggable>
  );
};

// PropTypes validation
DraggableSticker.propTypes = {
  sticker: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    svgPath: PropTypes.string.isRequired,
    size: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    rotation: PropTypes.number,
    isFlipped: PropTypes.bool,
    zIndex: PropTypes.number,
    isPartOfPack: PropTypes.bool,
    packId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    packIndex: PropTypes.number,
    dimensions: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    etsyUrl: PropTypes.string
  }).isRequired,
  widthPx: PropTypes.number.isRequired,
  heightPx: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  onUpdateSticker: PropTypes.func.isRequired,
  showDetails: PropTypes.bool,
  zoomScale: PropTypes.number.isRequired,
  allStickers: PropTypes.array.isRequired,
  adminMode: PropTypes.bool,
  onDragStateChange: PropTypes.func
};

export default DraggableSticker;
